<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { z } from 'zod';
import { UiAside, UiFormBuilder, UiButton, UiText, UiIcon } from '@shopexperts/shared';
import type { BuilderRowConfig } from '@shopexperts/shared';
import type { DateValue } from '@shopexperts/shared/dist/types/ui/UiDatePicker.vue';
import type { ExpertPlan, ListExpertSubscriptionInvoicesItem } from '@shopexperts/legacy-api';
import { sanitizeHttpUrl } from '@/app/utils/httpUrl';

const DEFAULT_CURRENCY = 'USD';
const CURRENCY_CODE_LENGTH = 3;

const dollarAmountSchema = z
  .string()
  .min(1, 'Amount is required')
  .refine((value) => /^\d+(\.\d{1,2})?$/.test(value), 'Enter a valid dollar amount');

const optionalDollarAmountSchema = z
  .string()
  .optional()
  .refine((value) => !value || /^\d+(\.\d{1,2})?$/.test(value), 'Enter a valid dollar amount');

const optionalUrlSchema = z
  .string()
  .optional()
  .refine((value) => !value || sanitizeHttpUrl(value) !== null, 'Enter a valid HTTP(S) URL');

const optionalPositiveIntegerSchema = z
  .string()
  .optional()
  .refine((value) => !value || /^\d+$/.test(value), 'Enter a whole number of days')
  .refine((value) => !value || Number(value) > 0, 'Term override must be greater than 0');

const invoiceStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'paid', label: 'Paid' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'void', label: 'Void' },
] as const;

export type InvoiceFormSubmitData = {
  v2_plan_id: number;
  invoice_type: 'external';
  amount: string;
  currency: string;
  issued_at: string;
  paid_at?: string | null;
  term_days_override?: number | null;
  invoice_url?: string;
  discount?: string;
  discount_note?: string | null;
  status?: string;
};

type InvoiceFormMode = 'create' | 'edit';
type SingleDateInput = DateValue | string | undefined;

type FormValues = {
  v2_plan_id: string;
  amount: string;
  currency: string;
  issued_at: string;
  paid_at: string;
  term_days_override: string;
  invoice_url: string;
  discount: string;
  discount_note: string;
  status: string;
};

type SubmitValues = {
  v2_plan_id: string;
  amount: string;
  currency: string;
  issued_at: string;
  paid_at?: string;
  term_days_override?: string;
  invoice_url?: string;
  discount?: string;
  discount_note?: string;
  status?: string;
};

const props = withDefaults(defineProps<{
  plans: ExpertPlan[];
  defaultTermDays?: number | null;
  invoice?: ListExpertSubscriptionInvoicesItem | null;
  isSubmitting?: boolean;
  mode?: InvoiceFormMode;
}>(), {
  defaultTermDays: null,
  invoice: null,
  isSubmitting: false,
  mode: 'create',
});

const open = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  submit: [data: InvoiceFormSubmitData];
}>();

const formModel = ref<FormValues>(createInitialValues());

const formSchema = computed(() => z.object({
  v2_plan_id: z.string().min(1, 'Plan is required'),
  amount: dollarAmountSchema,
  currency: z
    .string()
    .trim()
    .min(1, 'Currency is required')
    .length(CURRENCY_CODE_LENGTH, 'Use a 3-letter currency code'),
  issued_at: z.string().min(1, 'Issued date is required'),
  paid_at: props.mode === 'create'
    ? z.string().min(1, 'Paid date is required')
    : z.string().optional(),
  term_days_override: optionalPositiveIntegerSchema,
  invoice_url: optionalUrlSchema,
  discount: optionalDollarAmountSchema,
  discount_note: z.string().optional(),
  status: props.mode === 'edit'
    ? z.string().min(1, 'Status is required')
    : z.string().optional(),
}));

const planOptions = computed(() =>
  props.plans.map((plan) => ({
    value: String(plan.id),
    label: `${plan.name} — ${(plan.price_cents / 100).toFixed(2)} ${plan.currency}/${plan.billing_cycle}`,
  })),
);

const asideTitle = computed(() => props.mode === 'edit' ? 'Edit Invoice' : 'Register Invoice');
const submitLabel = computed(() => props.mode === 'edit' ? 'Save Changes' : 'Register Invoice');
const isEditMode = computed(() => props.mode === 'edit');
const amountLabel = computed(() => `Amount (${formModel.value.currency || DEFAULT_CURRENCY})`);
const discountLabel = computed(() => `Discount (${formModel.value.currency || DEFAULT_CURRENCY})`);
const defaultTermDaysLabel = computed(() => props.defaultTermDays);

const rows = computed((): BuilderRowConfig[] => {
  const baseRows: BuilderRowConfig[] = [
    {
      columns: [
        {
          name: 'v2_plan_id',
          type: 'select' as const,
          label: 'Plan',
          props: {
            options: planOptions.value,
            placeholder: 'Select a plan',
            disabled: props.mode === 'edit',
          },
        },
      ],
    },
    {
      columns: [
        {
          name: 'amount',
          type: 'number' as const,
          label: amountLabel.value,
          props: {
            placeholder: 'e.g. 49.00',
            step: 0.01,
            min: 0,
          },
        },
        {
          name: 'currency',
          type: 'text' as const,
          label: 'Currency',
          props: {
            placeholder: 'USD',
          },
        },
      ],
    },
    {
      columns: [
        {
          name: 'issued_at',
          type: 'date' as const,
          label: 'Issued Date',
          props: { placeholder: 'Select issued date' },
        },
        {
          name: 'paid_at',
          type: 'date' as const,
          label: props.mode === 'edit' ? 'Paid Date (optional)' : 'Paid Date',
          props: { placeholder: 'Select paid date' },
        },
      ],
    },
    {
      columns: [
        {
          name: 'term_days_override',
          type: 'number' as const,
          label: 'Term Override (days)',
          hint: defaultTermDaysLabel.value
            ? `Leave blank to use the default ${defaultTermDaysLabel.value}-day term.`
            : 'Leave blank to use the current default term.',
          props: {
            placeholder: defaultTermDaysLabel.value ? String(defaultTermDaysLabel.value) : 'Default term',
            min: 1,
            step: 1,
          },
        },
      ],
    },
    {
      columns: [
        {
          name: 'invoice_url',
          type: 'url' as const,
          label: 'Invoice URL',
          props: { placeholder: 'https://...' },
        },
      ],
    },
    {
      columns: [
        {
          name: 'discount',
          type: 'number' as const,
          label: discountLabel.value,
          props: {
            placeholder: '0.00',
            step: 0.01,
            min: 0,
          },
        },
        {
          name: 'discount_note',
          type: 'text' as const,
          label: 'Discount Note',
          props: { placeholder: 'Optional note' },
        },
      ],
    },
  ];

  if (props.mode === 'edit') {
    baseRows.push({
      columns: [
        {
          name: 'status',
          type: 'select' as const,
          label: 'Status',
          props: {
            options: [...invoiceStatusOptions],
            placeholder: 'Select a status',
          },
        },
      ],
    });
  }

  return baseRows;
});

watch(
  () => [props.mode, props.invoice, props.plans.length, open.value] as const,
  ([, , , isOpen]) => {
    if (isOpen) {
      formModel.value = createInitialValues();
    }
  },
  { immediate: true },
);

watch(
  () => formModel.value.v2_plan_id,
  (planId, previousPlanId) => {
    const selectedPlan = props.plans.find((plan) => String(plan.id) === planId);
    if (!selectedPlan) return;

    if (props.mode === 'create' && (!previousPlanId || previousPlanId !== planId)) {
      formModel.value.amount = centsToDollars(selectedPlan.price_cents);
      formModel.value.currency = normalizeCurrency(selectedPlan.currency);
    }
  },
);

watch(
  () => formModel.value.issued_at,
  (value) => {
    const normalizedValue = normalizeDateValue(value);
    if (normalizedValue !== value) {
      formModel.value.issued_at = normalizedValue;
    }
  },
);

watch(
  () => formModel.value.paid_at,
  (value) => {
    const normalizedValue = normalizeDateValue(value);
    if (normalizedValue !== value) {
      formModel.value.paid_at = normalizedValue;
    }
  },
);

watch(
  () => formModel.value.currency,
  (value) => {
    const normalizedValue = normalizeCurrency(value);
    if (normalizedValue !== value) {
      formModel.value.currency = normalizedValue;
    }
  },
);

function createInitialValues(): FormValues {
  if (props.mode === 'edit' && props.invoice) {
    return {
      v2_plan_id: String(props.invoice.v2_plan_id),
      amount: centsToDollars(props.invoice.amount_cents),
      currency: normalizeCurrency(props.invoice.currency),
      issued_at: normalizeDateValue(props.invoice.issued_at ?? ''),
      paid_at: normalizeDateValue(props.invoice.paid_at ?? ''),
      term_days_override: props.invoice.term_days_override ? String(props.invoice.term_days_override) : '',
      invoice_url: props.invoice.invoice_url ?? '',
      discount: props.invoice.discount_cents ? centsToDollars(props.invoice.discount_cents) : '',
      discount_note: props.invoice.discount_note ?? '',
      status: props.invoice.status ?? 'issued',
    };
  }

  return {
    v2_plan_id: '',
    amount: '',
    currency: props.plans[0]?.currency ? normalizeCurrency(props.plans[0].currency) : DEFAULT_CURRENCY,
    issued_at: '',
    paid_at: '',
    term_days_override: '',
    invoice_url: '',
    discount: '',
    discount_note: '',
    status: 'issued',
  };
}

function onSubmit(values: SubmitValues) {
  const normalizedPaidAt = values.paid_at ? formatDateToYYYYMMDD(values.paid_at) : null;
  const normalizedTermDaysOverride = values.term_days_override ? Number(values.term_days_override) : null;

  const data: InvoiceFormSubmitData = {
    v2_plan_id: Number(values.v2_plan_id),
    invoice_type: 'external',
    amount: values.amount,
    currency: normalizeCurrency(values.currency),
    issued_at: formatDateToYYYYMMDD(values.issued_at),
    paid_at: props.mode === 'edit' ? normalizedPaidAt : normalizedPaidAt ?? '',
    ...(normalizedTermDaysOverride !== null
      ? { term_days_override: normalizedTermDaysOverride }
      : props.mode === 'edit'
        ? { term_days_override: null }
        : {}),
    ...(values.invoice_url ? { invoice_url: values.invoice_url } : {}),
    ...(values.discount ? { discount: values.discount } : { discount: '0.00' }),
    ...(values.discount_note ? { discount_note: values.discount_note } : props.mode === 'edit' ? { discount_note: null } : {}),
    ...(props.mode === 'edit' ? { status: values.status } : {}),
  };

  emit('submit', data);
}

function handleClose() {
  open.value = false;
  formModel.value = createInitialValues();
}

function formatDateToYYYYMMDD(date: SingleDateInput): string {
  if (!date) return '';
  if (typeof date === 'string') return date;

  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  return `${date.year}-${month}-${day}`;
}

function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function normalizeCurrency(currency: string | null | undefined): string {
  return (currency || DEFAULT_CURRENCY).trim().toUpperCase();
}

function normalizeDateValue(date: SingleDateInput): string {
  return formatDateToYYYYMMDD(date);
}
</script>

<template>
  <UiAside v-model="open" position="right" :close-on-overlay-click="false">
    <div class="px-4 py-6 md:p-8 flex flex-col gap-4 h-full overflow-y-auto">
      <div class="flex items-center justify-between">
        <div>
          <UiText as="h3" variant="h3" :text="asideTitle" />
          <UiText
            as="p"
            variant="p-small"
            class="text-gray-500 mt-1"
          >
            {{ isEditMode ? 'Update billing details, discount context, and invoice status.' : 'Create an external invoice with the subscription pricing and discount context.' }}
          </UiText>
        </div>
        <UiButton variant="ghost" shape="square" @click="handleClose">
          <template #before_label>
            <UiIcon name="close" class="w-5 h-5" />
          </template>
        </UiButton>
      </div>

      <UiFormBuilder
        :schema="formSchema"
        :rows="rows"
        v-model="formModel"
        scroll-to-first-error
        @submit="onSubmit"
      >
        <template #actions>
          <div class="flex justify-end gap-3 pt-4">
            <UiButton variant="outline" label="Cancel" @click="handleClose" />
            <UiButton type="submit" variant="primary" :label="submitLabel" :loading="isSubmitting" />
          </div>
        </template>
      </UiFormBuilder>
    </div>
  </UiAside>
</template>
